import { EventBus, IReactiveModel, ViewModel } from '@descomplica/microfrontend-core';
import { action, computed, makeObservable, observable } from 'mobx';

import { GetLibraryByClassifierCollaboration } from '@/application/collaborations/get-library-by-classifier.collaboration';
import { GetLiveStreamingByHighestClassificationLevelCollaboration } from '@/application/collaborations/get-live-streaming-by-highest-classification-level.collaboration';
import type { ClassifierDto, LiveStreamingDto } from '@/application/interfaces/pegasus.gateway';
import { Tracking } from '@/application/utils/tracking';
import { SelectClassifierEvent } from '@/presentation/events/select-classifier.event';
import { ApplicationModel } from '@/presentation/models/application.model';
import { ClassifiersModel } from '@/presentation/models/classifiers.model';
import { UserModel } from '@/presentation/models/user.model';

@ViewModel({ singleton: true })
export class LibraryViewModel implements IReactiveModel {
  @observable isLoading = true;
  @observable hasError = false;
  @observable classifiers: ClassifierDto[] = [];
  @observable liveStreaming: LiveStreamingDto;

  constructor(
    private readonly eventBus: EventBus,
    private readonly applicationModel: ApplicationModel,
    private readonly getLibraryByClassifierCollaboration: GetLibraryByClassifierCollaboration,
    private readonly liveStreamingCollaboration: GetLiveStreamingByHighestClassificationLevelCollaboration,
    private readonly userModel: UserModel,
    private readonly classifiersModel: ClassifiersModel,
  ) {
    makeObservable(this);

    this.eventBus.on(SelectClassifierEvent, async () => {
      this.getLibrary();
    });
  }

  @action
  async onInit() {
    Tracking.pageView();

    this.getLibrary();
    this.getLiveStreaming(this.classifiersModel.selectedClassifier.guid);
  }

  @action
  setClassifiers(classifiers: ClassifierDto[]) {
    this.classifiers = classifiers;
  }

  @action.bound
  async getLiveStreaming(guid: string) {
    try {
      const data = await this.liveStreamingCollaboration.run(guid);
      this.setLiveStreaming(data.data);
    } catch (error) {
      console.error('Error getting live streaming', error);
    }
  }

  @action
  setLiveStreaming(liveStreaming: LiveStreamingDto) {
    this.liveStreaming = liveStreaming;
  }

  @action.bound
  async getLibrary() {
    try {
      this.isLoading = true;

      const classifierGuid = this.classifiersModel.selectedClassifier.guid;

      const data = await this.getLibraryByClassifierCollaboration.run(classifierGuid);

      this.setClassifiers(data.data);

      this.isLoading = false;
    } catch (error) {
      console.error(error);
      this.hasError = true;
    } finally {
      this.isLoading = false;
    }
  }

  @computed
  get application() {
    return this.applicationModel.application;
  }

  @computed
  get user() {
    return this.userModel.user;
  }
}
